import {
  Card,
  Checkbox,
  Grid,
  LinearProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { CNPJ_FORMATTED_REGEX } from '@/utils/cnpj';
import { CPF_FORMATTED_REGEX } from '@/utils/cpf';
import Scrollbar from '../scrollbar/Scrollbar';
import TableActionsRenderer from './TableActionsRenderer';
import TableHeadCustom from './TableHeadCustom';
import TableSelectedAction from './TableSelectedAction';
import {
  HIDE_BELOW_KEYS,
  hasHiddenColumns,
  resolveCardsSx,
  resolveHiddenBodyColumnsSx,
  resolveResponsiveTableSx,
} from './responsiveColumns';

// O rótulo ordenável capitaliza (TableSortLabel); o fixo mantém a mesma grafia de antes.
const HEAD_SX = { '& .MuiTableCell-head': { textTransform: 'capitalize' } };

const SearchableTable = ({
  size = 'medium',
  table,
  count,
  rows: rawRows,
  labels,
  fetching,
  infiniteLoading = false,
  selectable = false,
  searchable = true,
  filterable = false,
  pageable = true,
  scrollable = true,
  inMemoryFilter = null,
  renderRow,
  layout = 'auto',
  cardsBelow,
  onRowClick,
  Actions: ActionsProp,
  noRadius = false,
  disableCardWrapper = false,
  renderFooter = null,
  tableSx,
  tableContainerSx,
  emptyStateText = 'Nenhum registro encontrado',
  emptySearchStateText = 'Nenhum resultado encontrado para a busca',
  emptyStateAction = null,
  emptyStateContent = null,
  getRowSx,
  selectedActions = null,
  selectedLabel,
  getRowSelectLabel,
  sx,
  minWidth,
  ...props
}) => {
  const renderScrollable = (children) => {
    if (scrollable) {
      return <Scrollbar>{children}</Scrollbar>;
    }
    return children;
  };

  const rows = useMemo(() => rawRows || [], [rawRows]);

  const filtered = useMemo(() => {
    if (inMemoryFilter) {
      return rows.filter(inMemoryFilter);
    }

    if (fetching) return [];

    if (!rows) return [];

    return rows;
  }, [rows, inMemoryFilter, fetching]);

  const handleCpfCnpj = (value) => {
    if (CPF_FORMATTED_REGEX.test(value) || CNPJ_FORMATTED_REGEX.test(value)) {
      return value.replace(/\.|-|\//g, '');
    }
    return value;
  };

  const actionsNode =
    typeof ActionsProp === 'function' ? <ActionsProp table={table} /> : ActionsProp;

  const sxArray = useMemo(() => {
    if (!sx) return [];
    return Array.isArray(sx) ? sx : [sx];
  }, [sx]);

  const tableContainerSxList = useMemo(() => {
    if (!tableContainerSx) return [];
    return Array.isArray(tableContainerSx) ? tableContainerSx : [tableContainerSx];
  }, [tableContainerSx]);

  const tableSxList = useMemo(() => {
    if (!tableSx) return [];
    return Array.isArray(tableSx) ? tableSx : [tableSx];
  }, [tableSx]);

  const isResponsiveColumns = useMemo(() => hasHiddenColumns(labels), [labels]);

  // O cabeçalho só vira botão na coluna que declara `sortable: true`. Sem nenhuma, o `onSort`
  // não desce: o clique gravava `orderBy` num estado que nenhuma consulta lia, e a coluna
  // parecia quebrada (CKTPL-1610). Quem liga o `sortable` passa `defaultOrderBy`/`defaultOrder`
  // iguais ao padrão da API: o `useTable` nasce em `name`/`asc`, e o cabeçalho marcaria uma
  // ordem que a consulta não aplicou.
  const hasSortableColumns = useMemo(
    () => Boolean(labels?.some((label) => label?.sortable)),
    [labels]
  );

  const headLabels = useMemo(
    () =>
      hasSortableColumns
        ? labels.map((label) => ({ ...label, sortable: label.sortable === true }))
        : labels,
    [labels, hasSortableColumns]
  );

  const resolvedMinWidth = props.minWidth || 720;

  const responsiveSxList = useMemo(() => {
    if (!isResponsiveColumns) return [];

    const responsiveTableSx = resolveResponsiveTableSx({
      labels,
      minWidth: resolvedMinWidth,
      layout,
    });
    const hiddenColumnsSx = resolveHiddenBodyColumnsSx(labels, { selectable });

    return [
      ...(responsiveTableSx ? [responsiveTableSx] : []),
      ...(hiddenColumnsSx ? [hiddenColumnsSx] : []),
    ];
  }, [isResponsiveColumns, labels, selectable, resolvedMinWidth, layout]);

  // Por último no array: dentro do breakpoint o card precisa vencer o `minWidth`, o
  // `tableLayout` e o `nowrap` que o consumidor declara pensando no scroll horizontal.
  const cardsSxList = useMemo(() => {
    const cardsSx = resolveCardsSx({ labels, cardsBelow, selectable });
    return cardsSx ? [cardsSx] : [];
  }, [labels, cardsBelow, selectable]);

  const cardSx = noRadius ? [{ borderRadius: 0 }, ...sxArray] : sx;

  // Busca ativa sem correspondencia e um estado diferente de uma listagem naturalmente vazia.
  const hasSearchTerm = Boolean(table?.search);

  let emptyStateNode = (
    <Stack width={1} alignItems="center" justifyContent="center" gap={1} p={2}>
      <Typography variant="caption" color="text.secondary">
        {hasSearchTerm ? emptySearchStateText : emptyStateText}
      </Typography>
      {emptyStateAction}
    </Stack>
  );

  if (emptyStateContent) {
    emptyStateNode = emptyStateContent;
  }

  const content = (
    <>
      {(searchable || filterable || actionsNode) && (
        <TableActionsRenderer
          searchable={searchable}
          filterable={filterable}
          table={table}
          fetching={fetching}
          onSearch={(value) => table.setSearch(handleCpfCnpj(value))}
          Actions={actionsNode}
        />
      )}

      <TableContainer
        sx={[
          {
            overflow: 'unset',
            width: '100%',
            '@media (max-width: 960px)': {
              overflowX: 'auto',
              overflow: 'auto',
            },
          },
          ...tableContainerSxList,
        ]}
      >
        {fetching && <LinearProgress />}

        {selectable && (
          <TableSelectedAction
            dense={table.dense}
            numSelected={table.selected.length}
            rowCount={rows.length}
            action={selectedActions}
            label={selectedLabel ? selectedLabel(table.selected.length) : undefined}
            onSelectAllRows={(checked) =>
              table.onSelectAllRows(
                checked,
                rows.map((row) => row.id || row.short_id)
              )
            }
          />
        )}

        {renderScrollable(
          <Table
            sx={[
              { minWidth: resolvedMinWidth, tableLayout: layout },
              ...responsiveSxList,
              ...tableSxList,
              ...cardsSxList,
            ]}
            size={size}
            stickyHeader
          >
            <TableHeadCustom
              headLabel={headLabels}
              rowCount={rows?.length}
              numSelected={table.selected.length}
              order={hasSortableColumns ? table.order : undefined}
              orderBy={hasSortableColumns ? table.orderBy : undefined}
              onSort={hasSortableColumns ? table.onSort : undefined}
              noRadius={noRadius}
              sx={HEAD_SX}
              onSelectAllRows={
                selectable &&
                ((checked) =>
                  table.onSelectAllRows(
                    checked,
                    rows.map((row) => row.id || row.short_id)
                  ))
              }
            />

            <TableBody>
              {filtered.map((row, index) => {
                const uniqueKey = row.id || row.short_id || `row-${index}`;

                return (
                  <TableRow
                    key={uniqueKey}
                    hover
                    {...(onRowClick ? { role: 'button', tabIndex: -1 } : {})}
                    onClick={() => onRowClick && onRowClick(row)}
                    sx={[
                      {
                        cursor: onRowClick ? 'pointer' : 'default',
                      },
                      getRowSx ? getRowSx(row, index) : null,
                    ]}
                  >
                    {selectable && (
                      <TableCell sx={{ pl: 1 }}>
                        <Checkbox
                          checked={table.selected.includes(row.id || row.short_id)}
                          inputProps={
                            getRowSelectLabel ? { 'aria-label': getRowSelectLabel(row) } : undefined
                          }
                          onDoubleClick={() => { }}
                          onClick={(event) => {
                            event.stopPropagation();
                            table.onSelectRow(row.id || row.short_id);
                          }}
                        />
                      </TableCell>
                    )}

                    {renderRow(row, index)}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!fetching && filtered.length === 0 && emptyStateNode}

        {infiniteLoading && !fetching && (
          <Stack width={1} alignItems="center" justifyContent="center" p={2}>
            <Typography variant="caption" color="text.secondary">
              Carregando...
            </Typography>
          </Stack>
        )}
      </TableContainer>

      {renderFooter
        ? renderFooter({ count, rows, filteredRows: filtered, table })
        : count > 0 &&
        pageable && (
          <Grid
            paddingX={2}
            paddingY={2}
            alignItems="center"
            justifyContent="center"
            container
            marginTop={1}
          >
            <Pagination
              count={Math.ceil(count / table.rowsPerPage)}
              page={+table.page || 1}
              onChange={table.onChangePage}
            />
          </Grid>
        )}
    </>
  );

  if (disableCardWrapper) {
    return content;
  }

  return (
    <Card {...props} sx={cardSx}>
      {content}
    </Card>
  );
};

SearchableTable.propTypes = {
  fetching: PropTypes.bool,
  labels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      hideBelow: PropTypes.oneOf(HIDE_BELOW_KEYS),
      sortable: PropTypes.bool,
    })
  ),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  Actions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  rows: PropTypes.array,
  renderRow: PropTypes.func,
  count: PropTypes.number,
  selectable: PropTypes.bool,
  searchable: PropTypes.bool,
  pageable: PropTypes.bool,
  filterable: PropTypes.bool,
  onRowClick: PropTypes.func,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  table: PropTypes.object,
  size: PropTypes.string,
  layout: PropTypes.oneOf(['auto', 'fixed']),
  cardsBelow: PropTypes.oneOf(HIDE_BELOW_KEYS),
  noRadius: PropTypes.bool,
  scrollable: PropTypes.bool,
  inMemoryFilter: PropTypes.func,
  infiniteLoading: PropTypes.bool,
  disableCardWrapper: PropTypes.bool,
  renderFooter: PropTypes.func,
  tableSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  tableContainerSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  emptyStateText: PropTypes.string,
  emptySearchStateText: PropTypes.string,
  emptyStateAction: PropTypes.node,
  emptyStateContent: PropTypes.node,
  getRowSx: PropTypes.func,
  selectedActions: PropTypes.node,
  selectedLabel: PropTypes.func,
  getRowSelectLabel: PropTypes.func,
};

export default SearchableTable;
